import { LeftOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons"
import { Button, Segmented, Skeleton, Tag } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { cn } from "../../utils/cn.ts"
import { CalendarEventFormModal } from "./components/CalendarEventFormModal.tsx"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { PortalResponsiveModal } from "./components/PortalResponsiveModal.tsx"
import { CalendarEventDetailModal } from "./components/CalendarEventDetailModal.tsx"
import {
  asAppointmentItems,
  groupItemsByDay,
  itemIsCancelled,
  layoutDayTimeBlocks,
  milestonesAsCalendarItems
} from "./helpers/calendar.ts"
import { fetchPortalCalendarEvents, fetchPortalProjectDetail } from "./redux/portal.thunks.ts"
import type { PortalCalendarItem } from "./types/portal.types.ts"

type ViewMode = "month" | "week" | "schedule"

const HOUR_HEIGHT = 48
const HOURS = Array.from({ length: 24 }, (_, i) => i)
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

const ITEM_DOT_CLASS: Record<string, string> = {
  appointment: "bg-primary",
  milestone: "bg-gray-400"
}

function ItemChip({ item, onOpen }: { item: PortalCalendarItem; onOpen: () => void }) {
  const isCancelled = itemIsCancelled(item)
  const time = item.kind === "appointment" && !item.all_day ? dayjs(item.start_at).format("h:mma") : null
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onOpen()
      }}
      className={cn(
        "flex w-full items-center gap-1.5 truncate rounded px-1.5 py-1 text-left text-sm hover:bg-gray-50",
        isCancelled && "text-gray-400 line-through"
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", ITEM_DOT_CLASS[item.kind])} />
      {time && <span className="shrink-0 text-gray-400">{time}</span>}
      <span className="truncate">{item.title}</span>
    </button>
  )
}

function AgendaDay({
  date,
  items,
  onOpen
}: {
  date: Dayjs
  items: PortalCalendarItem[]
  onOpen: (item: PortalCalendarItem) => void
}) {
  return (
    <div className="flex gap-4 border-b border-gray-50 py-3 last:border-0">
      <div className="w-16 shrink-0 text-sm">
        <p className="font-semibold text-gray-900">{date.format("ddd")}</p>
        <p className="text-gray-500">{date.format("MMM D")}</p>
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {items.map((item) => (
          <button
            key={`${item.kind}-${item.id}`}
            type="button"
            onClick={() => onOpen(item)}
            className={cn(
              "flex flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-gray-50",
              itemIsCancelled(item) && "opacity-60"
            )}
          >
            <span
              className={cn(
                "text-sm font-medium text-gray-900",
                itemIsCancelled(item) && "line-through"
              )}
            >
              {item.title}
            </span>
            <span className="text-sm text-gray-500">
              {item.project_name}
              {item.kind === "appointment" && !item.all_day && (
                <> · {dayjs(item.start_at).format("h:mm A")}</>
              )}
              {item.kind === "appointment" && item.location && <> · {item.location}</>}
              {item.kind === "milestone" && <> · estimated</>}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function PortalCalendarPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isMobile = useAppSelector((state) => state.nav.isMobile)
  const selectedProjectId = useAppSelector((state) => state.portal.selectedProjectId)
  const projects = useAppSelector((state) => state.portal.projects)
  const projectsLoaded = useAppSelector((state) => state.portal.projectsLoaded)
  const events = useAppSelector((state) => state.portal.calendarEvents)
  const fetching = useAppSelector((state) => state.portal.calendarEventsFetching)
  const loaded = useAppSelector((state) => state.portal.calendarEventsLoaded)
  const failed = useAppSelector((state) => state.portal.calendarEventsError)
  const projectDetail = useAppSelector((state) => state.portal.projectDetail)

  const [view, setView] = useState<ViewMode>(isMobile ? "schedule" : "month")
  const [anchor, setAnchor] = useState(() => dayjs())
  const [selectedItem, setSelectedItem] = useState<PortalCalendarItem | null>(null)
  const [dayAgendaDate, setDayAgendaDate] = useState<Dayjs | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formInitialDate, setFormInitialDate] = useState<Dayjs | null>(null)
  const weekScrollRef = useRef<HTMLDivElement>(null)
  const [weekGridMaxHeight, setWeekGridMaxHeight] = useState(600)

  // "Today" and the current-time line both read this — one clock, in the
  // zone the grid itself is drawn in (the same `timezone` shown in the
  // header), rather than each computing "now" a different way. Ticks every
  // minute, which is as often as the line's position can visibly change.
  const [nowTick, setNowTick] = useState(() => dayjs().tz(timezone))
  useEffect(() => {
    const id = setInterval(() => setNowTick(dayjs().tz(timezone)), 60000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (isMobile && view === "week") setView("schedule")
  }, [isMobile, view])

  const refetch = () => void dispatch(fetchPortalCalendarEvents({ projectId: selectedProjectId }))

  useEffect(() => {
    refetch()
  }, [dispatch, selectedProjectId])

  useEffect(() => {
    if (selectedProjectId) void dispatch(fetchPortalProjectDetail(selectedProjectId))
  }, [dispatch, selectedProjectId])

  // No push/live sync exists for the portal calendar yet — a change made
  // from the GC's side only reaches this tab when it refetches. Re-check on
  // tab focus/visibility, the same pattern already used for an open Quick
  // View elsewhere in the app, rather than claiming real-time updates.
  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === "visible") refetch()
    }
    document.addEventListener("visibilitychange", handleFocus)
    window.addEventListener("focus", handleFocus)
    return () => {
      document.removeEventListener("visibilitychange", handleFocus)
      window.removeEventListener("focus", handleFocus)
    }
  }, [selectedProjectId])

  useEffect(() => {
    if (view === "week" && weekScrollRef.current) {
      weekScrollRef.current.scrollTop = 7 * HOUR_HEIGHT
    }
  }, [view, anchor])

  // The week grid fills whatever room is actually left below it, measured
  // against the real viewport rather than a guessed `calc(100vh - Npx)` —
  // day headers and the all-day row stay outside this measurement (and
  // outside the scroll region) so they're never pushed off-screen.
  useEffect(() => {
    if (view !== "week") return
    const recompute = () => {
      const node = weekScrollRef.current
      if (!node) return
      const top = node.getBoundingClientRect().top
      setWeekGridMaxHeight(Math.max(320, Math.floor(window.innerHeight - top - 24)))
    }
    recompute()
    window.addEventListener("resize", recompute)
    return () => window.removeEventListener("resize", recompute)
  }, [view, anchor, isMobile])

  const items = useMemo<PortalCalendarItem[]>(() => {
    const appointments = asAppointmentItems(events)
    const milestones = selectedProjectId ? milestonesAsCalendarItems(projectDetail) : []
    return [...appointments, ...milestones]
  }, [events, projectDetail, selectedProjectId])

  const grouped = useMemo(() => groupItemsByDay(items), [items])

  const goToday = () => setAnchor(dayjs())
  const goPrev = () =>
    setAnchor((current) =>
      view === "month"
        ? current.subtract(1, "month")
        : view === "week"
          ? current.subtract(1, "week")
          : current.subtract(14, "day")
    )
  const goNext = () =>
    setAnchor((current) =>
      view === "month"
        ? current.add(1, "month")
        : view === "week"
          ? current.add(1, "week")
          : current.add(14, "day")
    )

  const openCreate = (date: Dayjs | null) => {
    setFormInitialDate(date)
    setFormOpen(true)
  }

  const handleWeekSlotClick = (e: React.MouseEvent<HTMLDivElement>, day: Dayjs) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const offsetMinutes = ((e.clientY - rect.top) / HOUR_HEIGHT) * 60
    const snapped = Math.max(0, Math.round(offsetMinutes / 30) * 30)
    openCreate(day.startOf("day").add(snapped, "minute"))
  }

  const heading =
    view === "month"
      ? anchor.format("MMMM YYYY")
      : view === "week"
        ? `${anchor.startOf("week").format("MMM D")} – ${anchor.endOf("week").format("MMM D, YYYY")}`
        : t("portal.calendar.scheduleHeading", {
            from: anchor.format("MMM D"),
            to: anchor.add(13, "day").format("MMM D, YYYY")
          })

  if (!projectsLoaded) {
    return (
      <PortalPageContainer fullWidth>
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (projects.length === 0) {
    return (
      <PortalPageContainer fullWidth>
        <PortalPageHeader title={t("portal.calendar.title")} subtitle={t("portal.calendar.subtitle")} />
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      </PortalPageContainer>
    )
  }

  const renderMonth = () => {
    const startOfMonth = anchor.startOf("month")
    const gridStart = startOfMonth.startOf("week")
    const days = Array.from({ length: 42 }, (_, i) => gridStart.add(i, "day"))
    const today = nowTick
    return (
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-gray-100 bg-gray-100">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="bg-gray-50 px-2 py-1.5 text-center text-sm font-medium text-gray-500">
            {d}
          </div>
        ))}
        {days.map((day) => {
          const dayItems = grouped.get(day.format("YYYY-MM-DD")) ?? []
          const inMonth = day.month() === anchor.month()
          const isToday = day.isSame(today, "day")
          const visible = dayItems.slice(0, 3)
          return (
            <div
              key={day.toISOString()}
              role="button"
              tabIndex={0}
              onClick={() => openCreate(day.hour(9))}
              onKeyDown={(e) => {
                if (e.key === "Enter") openCreate(day.hour(9))
              }}
              className={cn(
                "flex min-h-28 flex-col bg-white p-1.5 hover:bg-gray-50/70 sm:min-h-32",
                !inMonth && "bg-gray-50/60"
              )}
            >
              <p
                className={cn(
                  "mb-1 inline-flex size-6 items-center justify-center rounded-full text-sm",
                  isToday
                    ? "bg-primary font-semibold text-white"
                    : inMonth
                      ? "text-gray-700"
                      : "text-gray-300"
                )}
              >
                {day.date()}
              </p>
              <div className="flex flex-col gap-0.5">
                {visible.map((item) => (
                  <ItemChip key={`${item.kind}-${item.id}`} item={item} onOpen={() => setSelectedItem(item)} />
                ))}
                {dayItems.length > 3 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDayAgendaDate(day)
                    }}
                    className="px-1.5 text-left text-sm text-gray-400 hover:text-primary"
                  >
                    {t("portal.calendar.dayAgendaMore", { count: dayItems.length - 3 })}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderWeek = () => {
    const start = anchor.startOf("week")
    const days = Array.from({ length: 7 }, (_, i) => start.add(i, "day"))
    const now = nowTick
    const nowMinutes = now.diff(now.startOf("day"), "minute")
    // Only when the visible week actually contains today — otherwise a
    // line at "now"'s time-of-day would land in the wrong week entirely.
    const showNowLine = !now.startOf("day").isBefore(start) && now.isBefore(start.add(7, "day"))
    return (
      <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="flex border-b border-gray-100">
          <div className="w-14 shrink-0" />
          {days.map((day) => {
            const isToday = day.isSame(now, "day")
            return (
              <div
                key={day.toISOString()}
                className={cn("flex-1 border-l border-gray-50 py-2 text-center", isToday && "bg-primary/5")}
              >
                <p className="text-sm text-gray-400">{day.format("ddd")}</p>
                <p className={cn("text-sm font-semibold", isToday ? "text-primary" : "text-gray-900")}>
                  {day.format("D")}
                </p>
              </div>
            )
          })}
        </div>

        <div className="flex border-b border-gray-100">
          <div className="w-14 shrink-0 py-1 pr-1 text-right text-[11px] uppercase text-gray-300">
            {t("portal.calendar.allDay")}
          </div>
          {days.map((day) => {
            const dayItems = (grouped.get(day.format("YYYY-MM-DD")) ?? []).filter(
              (item) => item.kind === "milestone" || (item.kind === "appointment" && item.all_day)
            )
            return (
              <div key={day.toISOString()} className="min-h-8 flex-1 border-l border-gray-50 p-0.5">
                {dayItems.map((item) => (
                  <ItemChip key={`${item.kind}-${item.id}`} item={item} onOpen={() => setSelectedItem(item)} />
                ))}
              </div>
            )
          })}
        </div>

        <div
          ref={weekScrollRef}
          className="relative flex overflow-y-auto"
          style={{ maxHeight: weekGridMaxHeight }}
        >
          <div className="w-14 shrink-0">
            {HOURS.map((h) => (
              <div key={h} style={{ height: HOUR_HEIGHT }} className="relative">
                <span className="absolute -top-2 right-1 text-[11px] text-gray-300">
                  {dayjs().hour(h).minute(0).format("h A")}
                </span>
              </div>
            ))}
          </div>

          {showNowLine && (
            <div
              className="pointer-events-none absolute inset-x-0 flex items-center"
              style={{ top: (nowMinutes / 60) * HOUR_HEIGHT }}
            >
              <div className="flex w-14 shrink-0 justify-end pr-1">
                <span className="-translate-y-1/2 whitespace-nowrap rounded bg-white px-1 text-[11px] font-medium text-red-500 shadow-sm">
                  {now.format("h:mm A")}
                </span>
              </div>
              {days.map((day) => {
                const isToday = day.isSame(now, "day")
                return (
                  <div key={day.toISOString()} className="relative flex-1">
                    <div className={cn("h-px w-full", isToday ? "bg-red-500" : "bg-red-300/50")} />
                    {isToday && (
                      <span className="absolute -left-1 top-1/2 size-2 -translate-y-1/2 rounded-full bg-red-500" />
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {days.map((day) => {
            const dayItems = (grouped.get(day.format("YYYY-MM-DD")) ?? []).filter(
              (item): item is Extract<PortalCalendarItem, { kind: "appointment" }> =>
                item.kind === "appointment" && !item.all_day
            )
            const blocks = layoutDayTimeBlocks(dayItems)
            return (
              <div
                key={day.toISOString()}
                className="relative flex-1 cursor-pointer border-l border-gray-50"
                style={{ height: 24 * HOUR_HEIGHT }}
                onClick={(e) => handleWeekSlotClick(e, day)}
              >
                {HOURS.map((h) => (
                  <div key={h} style={{ height: HOUR_HEIGHT }} className="border-b border-gray-50" />
                ))}
                {blocks.map(({ item, startMinute, durationMinutes, column, columns }) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedItem(item)
                    }}
                    className={cn(
                      "absolute overflow-hidden rounded-md border px-1.5 py-0.5 text-left text-sm",
                      itemIsCancelled(item)
                        ? "border-gray-200 bg-gray-100 text-gray-400 line-through"
                        : "border-primary/30 bg-primary/10 text-primary-800 hover:bg-primary/20"
                    )}
                    style={{
                      top: (startMinute / 60) * HOUR_HEIGHT,
                      height: Math.max((durationMinutes / 60) * HOUR_HEIGHT - 2, 20),
                      left: `${(column / columns) * 100}%`,
                      width: `calc(${100 / columns}% - 2px)`
                    }}
                  >
                    <span className="block truncate font-medium leading-tight">{item.title}</span>
                    <span className="block truncate text-[11px] leading-tight opacity-70">
                      {dayjs(item.start_at).format("h:mm A")}
                    </span>
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderSchedule = () => {
    const start = anchor.startOf("day")
    const days = Array.from({ length: 14 }, (_, i) => start.add(i, "day"))
    const daysWithItems = days.filter((d) => (grouped.get(d.format("YYYY-MM-DD")) ?? []).length > 0)
    if (daysWithItems.length === 0) {
      return (
        <PortalEmptyState
          title={t("portal.calendar.emptyTitle")}
          description={t("portal.calendar.emptyDescription")}
          action={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(anchor)}>
              {t("portal.calendar.createEmptyAction")}
            </Button>
          }
        />
      )
    }
    return (
      <div className="flex flex-col rounded-lg border border-gray-100 bg-white px-3">
        {daysWithItems.map((day) => (
          <AgendaDay
            key={day.toISOString()}
            date={day}
            items={grouped.get(day.format("YYYY-MM-DD")) ?? []}
            onOpen={setSelectedItem}
          />
        ))}
      </div>
    )
  }

  return (
    <PortalPageContainer fullWidth>
      <PortalPageHeader
        title={t("portal.calendar.title")}
        subtitle={t("portal.calendar.subtitle")}
        actions={
          <>
            <Segmented
              value={view}
              onChange={(value) => setView(value as ViewMode)}
              options={[
                ...(isMobile
                  ? []
                  : [
                      { label: t("portal.calendar.viewMonth"), value: "month" },
                      { label: t("portal.calendar.viewWeek"), value: "week" }
                    ]),
                { label: t("portal.calendar.viewSchedule"), value: "schedule" },
                ...(isMobile ? [{ label: t("portal.calendar.viewMonth"), value: "month" }] : [])
              ]}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(null)}>
              {t("portal.calendar.createEvent")}
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button onClick={goToday}>{t("portal.calendar.today")}</Button>
          <Button icon={<LeftOutlined />} onClick={goPrev} aria-label={t("portal.calendar.previous")} />
          <Button icon={<RightOutlined />} onClick={goNext} aria-label={t("portal.calendar.next")} />
          <h2 className="ml-1 text-left text-lg font-semibold text-gray-900">{heading}</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">
            {t("portal.calendar.timezoneLabel")}: {timezone}
          </span>
          {selectedProjectId ? (
            <Tag color="blue">{projects.find((p) => p.id === selectedProjectId)?.lead_name}</Tag>
          ) : (
            <Tag>{t("portal.projectSelector.allProjects")}</Tag>
          )}
        </div>
      </div>

      {fetching && !loaded ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : failed ? (
        <PortalEmptyState
          title={t("portal.calendar.loadError")}
          action={<Button onClick={refetch}>{t("portal.home.retry")}</Button>}
        />
      ) : view === "month" ? (
        renderMonth()
      ) : view === "week" ? (
        renderWeek()
      ) : (
        renderSchedule()
      )}

      <CalendarEventDetailModal
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />

      <CalendarEventFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initialDate={formInitialDate}
        initialProjectId={selectedProjectId}
      />

      <PortalResponsiveModal
        open={!!dayAgendaDate}
        onClose={() => setDayAgendaDate(null)}
        title={dayAgendaDate?.format("dddd, MMMM D")}
        width={440}
      >
        <div className="flex flex-col gap-1">
          {(dayAgendaDate ? (grouped.get(dayAgendaDate.format("YYYY-MM-DD")) ?? []) : []).map((item) => (
            <button
              key={`${item.kind}-${item.id}`}
              type="button"
              onClick={() => {
                setSelectedItem(item)
                setDayAgendaDate(null)
              }}
              className={cn(
                "flex flex-col items-start gap-0.5 rounded-md px-2 py-2 text-left hover:bg-gray-50",
                itemIsCancelled(item) && "opacity-60"
              )}
            >
              <span className={cn("text-sm font-medium text-gray-900", itemIsCancelled(item) && "line-through")}>
                {item.title}
              </span>
              <span className="text-sm text-gray-500">
                {item.project_name}
                {item.kind === "appointment" && !item.all_day && (
                  <> · {dayjs(item.start_at).format("h:mm A")}</>
                )}
              </span>
            </button>
          ))}
        </div>
      </PortalResponsiveModal>
    </PortalPageContainer>
  )
}

export default PortalCalendarPage
